import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'shared-ui';

import { OrderExecutionEntry } from 'api/dod/dod.types';
import { WebsocketContext } from 'context/websocket/websocketContext/WebsocketContext';
import { QueryKeyEnum } from 'core/global.enum';

type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

const MAX_HISTORY_ENTRIES = 500;
/** A drain burst pushes many events per second; one refetch every two seconds is plenty. */
const INVALIDATE_THROTTLE_MS = 2000;

export const useDodSocket = (): {
  connectionStatus: ConnectionStatus;
  historyEntries: OrderExecutionEntry[];
  clearHistory: () => void;
} => {
  const socket = useContext(WebsocketContext);
  const queryClient = useQueryClient();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [historyEntries, setHistoryEntries] = useState<OrderExecutionEntry[]>([]);
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  useEffect(() => {
    if (!socket) {
      return;
    }

    const doSubscribe = (): void => {
      socket.emit('dod.subscribe');
    };

    if (socket.connected) {
      setConnectionStatus('connected');
      doSubscribe();
    }

    // The v2 views key on these; invalidating them is what makes a push visible with refresh=off.
    const throttledUntil: Partial<Record<QueryKeyEnum, number>> = {};
    const trailing: Partial<Record<QueryKeyEnum, ReturnType<typeof setTimeout>>> = {};
    const fire = (key: QueryKeyEnum): void => {
      throttledUntil[key] = Date.now() + INVALIDATE_THROTTLE_MS;
      queryClientRef.current.invalidateQueries([key]);
    };
    // Leading edge plus one trailing refetch, so the last events of a burst are not lost.
    const invalidate = (...keys: QueryKeyEnum[]): void => {
      const now = Date.now();

      keys.forEach((key) => {
        const until = throttledUntil[key] ?? 0;

        if (until > now) {
          if (!trailing[key]) {
            trailing[key] = setTimeout(() => {
              delete trailing[key];
              fire(key);
            }, until - now);
          }

          return;
        }

        fire(key);
      });
    };

    const onQueueStatus = (): void => {
      invalidate(QueryKeyEnum.DOD_QUEUES, QueryKeyEnum.DOD_QUEUE_DETAIL_V2, QueryKeyEnum.DOD_OVERVIEW);
    };

    const onOrderExecuted = (entry: OrderExecutionEntry): void => {
      setHistoryEntries((prev) => {
        const next = [entry, ...prev];
        return next.length > MAX_HISTORY_ENTRIES ? next.slice(0, MAX_HISTORY_ENTRIES) : next;
      });
      invalidate(QueryKeyEnum.DOD_QUEUES, QueryKeyEnum.DOD_QUEUE_DETAIL_V2, QueryKeyEnum.DOD_OVERVIEW);
    };

    const onDlqAdded = (): void => {
      invalidate(QueryKeyEnum.DOD_DLQ, QueryKeyEnum.DOD_DLQ_SUMMARY, QueryKeyEnum.DOD_OVERVIEW);
    };

    const onConnect = (): void => {
      setConnectionStatus('connected');
      doSubscribe();
    };

    const onDisconnect = (): void => {
      setConnectionStatus('disconnected');
    };

    const onReconnectAttempt = (): void => {
      setConnectionStatus('reconnecting');
    };

    socket.on('dod.queue.status', onQueueStatus);
    socket.on('dod.order.executed', onOrderExecuted);
    socket.on('dod.dlq.added', onDlqAdded);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.io.on('reconnect_attempt', onReconnectAttempt);

    return () => {
      // Leave the room, otherwise the backend keeps computing the overview tick for a client
      // that has navigated away from the monitor.
      if (socket.connected) {
        socket.emit('dod.unsubscribe');
      }
      Object.values(trailing).forEach((timer) => timer && clearTimeout(timer));

      socket.off('dod.queue.status', onQueueStatus);
      socket.off('dod.order.executed', onOrderExecuted);
      socket.off('dod.dlq.added', onDlqAdded);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
    };
  }, [socket]);

  const clearHistory = useCallback(() => {
    setHistoryEntries([]);
  }, []);

  return { connectionStatus, historyEntries, clearHistory };
};
